/* global process */
import URLJoin from "url-join"

// ----------------------------------------------------------------------------

const DEBUG_API = process.env.NODE_ENV === "development"
const PROD_BASE_URL = "https://app.vebipro.it/api"
const DEV_BASE_URL = "http://localhost:3001"
const PROD_ACCESS_TOKEN = "uzdrctSqbbuMUUavGs0ySOf3AOLjzKdK"
const DEV_ACCESS_TOKEN = "uzdrctSqbbuMUUavGs0ySOf3AOLjzKdK"
const FORCE_PROD = false

// ----------------------------------------------------------------------------

function getBaseURL() {
  if (!FORCE_PROD && process.env.NODE_ENV === "development") {
    return DEV_BASE_URL
  } else {
    return PROD_BASE_URL
  }
}

function getAccessToken() {
  if (process.env.NODE_ENV === "development") {
    return DEV_ACCESS_TOKEN
  } else {
    return PROD_ACCESS_TOKEN
  }
}

const baseURL = getBaseURL()
const accessToken = getAccessToken()

// ----------------------------------------------------------------------------

export class APINotOkError extends Error {
  constructor(statusCode, serverResponse) {
    const message =
      (serverResponse && serverResponse.message) ||
      `HTTP status code: ${statusCode}`
    super(message)
    this.name = "APINotOkError"
    this.statusCode = statusCode
    this.serverMessage = serverResponse && serverResponse.state
    this.errorKey = serverResponse && serverResponse.errorKey
  }
}

// ----------------------------------------------------------------------------
// Auth
// ----------------------------------------------------------------------------

export function signUp({ ...userBody }) {
  const headers = {}
  const body = {
    ...userBody,
    access_token: accessToken,
  }

  return executePOSTRequest(URLJoin(baseURL, "/users"), null, body, headers)
}

export function signIn(email, password) {
  const base64 = require("js-base64").Base64
  var encoded = base64.encode(`${email}:${password}`)
  const headers = {
    Authorization: `Basic ${encoded}`,
  }

  const body = { access_token: accessToken }

  return executePOSTRequest(URLJoin(baseURL, "/auth"), null, body, headers)
}

// ----------------------------------------------------------------------------
// Create admin user
// ----------------------------------------------------------------------------

export function createAdminUser({ email, password, name, surname }) {
  const body = {
    email,
    password,
    name,
    surname,
    role: "admin",
  }

  return signUp(body)
}

// ----------------------------------------------------------------------------

export function getUser(token) {
  let url = "users/me"
  return executeGETRequest(url, token)
}

// ----------------------------------------------------------------------------
// Products
// ----------------------------------------------------------------------------

export async function getProductImportSideEffects(file, token) {
  const relativeUrl = "/products/import/info"

  const fullUrl = URLJoin(baseURL, relativeUrl)

  const body = {
    file,
  }

  const method = "POST"

  return executeFormDataRequest(fullUrl, method, null, body, token)
}

export async function importProductsFromCSV(file, token) {
  const relativeUrl = "/products/import"

  const fullUrl = URLJoin(baseURL, relativeUrl)

  const body = {
    file,
  }

  const method = "POST"

  return executeFormDataRequest(fullUrl, method, null, body, token)
}

// ----------------------------------------------------------------------------
// Dosage data
// ----------------------------------------------------------------------------

export function importDosageData(file, token) {
  const relativeURL = "/dosages/import"

  const fullURL = URLJoin(baseURL, relativeURL)

  const body = {
    file,
  }

  const method = "POST"

  return executeFormDataRequest(fullURL, method, null, body, token)
}

// ----------------------------------------------------------------------------
// Product Codes
// ----------------------------------------------------------------------------

export async function getProductCodesImportSideEffects(file, token) {
  const relativeUrl = "/product-codes/import/info"

  const fullUrl = URLJoin(baseURL, relativeUrl)

  const body = {
    file,
  }

  const method = "POST"

  return executeFormDataRequest(fullUrl, method, null, body, token)
}

export async function importProductCodesFromCSV(file, token) {
  const relativeUrl = "/product-codes/import"

  const fullUrl = URLJoin(baseURL, relativeUrl)

  const body = {
    file,
  }

  const method = "POST"

  return executeFormDataRequest(fullUrl, method, null, body, token)
}

// ----------------------------------------------------------------------------
// Categories
// ----------------------------------------------------------------------------

export function updateCategory(categoryId, body, token) {
  const url = `/categories/${categoryId}`

  return executePUTRequest(URLJoin(baseURL, url), token, body)
}

// ----------------------------------------------------------------------------
// Tickets
// ----------------------------------------------------------------------------

export async function createTicketMessage(ticketId, sentBy, text, token) {
  const relativeUrl = `/tickets/${ticketId}/messages`

  const fullUrl = URLJoin(baseURL, relativeUrl)

  const body = {
    sentBy: sentBy,
    text,
  }

  return executePOSTRequest(fullUrl, token, body)
}

export async function closeTicket(ticketId, token) {
  const relativeUrl = `/tickets/${ticketId}/close`

  const fullUrl = URLJoin(baseURL, relativeUrl)

  return executePUTRequest(fullUrl, token)
}

export function markTicketMessagesAsRead(ticketId, token) {
  const relativeUrl = `/tickets/${ticketId}/read`

  const fullUrl = URLJoin(baseURL, relativeUrl)

  return executePUTRequest(fullUrl, token)
}

export function getUserUnreadedMessages(token) {
  const relativeUrl = `/users/me/unreaded-messages`

  const fullUrl = URLJoin(baseURL, relativeUrl)

  return executeGETRequest(fullUrl, token)
}

// ----------------------------------------------------------------------------
// Posts
// ----------------------------------------------------------------------------

export async function createPost(values, token) {
  const relativeURL = "/posts"

  const fullUrl = URLJoin(baseURL, relativeURL)

  const headers = {}

  return executeFormDataRequest(fullUrl, "POST", headers, values, token)
}

export async function updatePost(postId, values, token) {
  const relativeURL = `/posts/${postId}`

  const fullUrl = URLJoin(baseURL, relativeURL)

  const headers = {}

  return executeFormDataRequest(fullUrl, "PUT", headers, values, token)
}

export async function publishPost(postId, token) {
  const relativeURL = `/posts/${postId}/publish`

  const fullUrl = URLJoin(baseURL, relativeURL)

  return executePOSTRequest(fullUrl, token)
}

// ----------------------------------------------------------------------------
// Assets
// ----------------------------------------------------------------------------

export function importAssetFile(file, relativeEndpoint = "", token) {
  let relativeURL = `/assets/${relativeEndpoint}`

  const fullUrl = URLJoin(baseURL, relativeURL)

  const headers = {}

  const body = {
    file,
  }

  return executeFormDataRequest(fullUrl, "POST", headers, body, token)
}

// ----------------------------------------------------------------------------
// REST Resources
// ----------------------------------------------------------------------------

export function getResourceList(resourceURI, token, options) {
  const { pageNumber, pageSize = 10, sort, filters } = options
  const url = URLJoin(baseURL, resourceURI)

  const filterParams = {}
  for (let filterKey in filters) {
    if (filters[filterKey].length) {
      if (Array.isArray(filters[filterKey])) {
        filterParams[filterKey] = filters[filterKey].join(",")
      } else {
        filterParams[filterKey] = filters[filterKey]
      }
    }
  }

  const params = {
    ...filterParams,
    limit: pageSize,
  }

  if (pageNumber) {
    params.page = pageNumber
  }

  if (sort) {
    const sortOperator = sort.order === "descend" ? "-" : "+"
    params.sort = `${sortOperator}${sort.key}`
  }

  const urlWithQuery = urlWithQueryParams(url, params)

  return executeGETRequest(urlWithQuery, token, { isList: true })
}

export function getResource(resourceURI, token) {
  const url = URLJoin(baseURL, resourceURI)
  return executeGETRequest(url, token, { isList: false })
}

export function createResource(resourceURI, token, resource) {
  const url = URLJoin(baseURL, resourceURI)
  return executePOSTRequest(url, token, resource)
}

export function updateResource(resourceURI, token, resourceID, resource) {
  const url = URLJoin(baseURL, resourceURI, resourceID)

  return executePUTRequest(url, token, resource)
}

export function deleteResource(resourceURI, token, resourceID) {
  const url = URLJoin(baseURL, resourceURI, resourceID)
  return executeDELETERequest(url, token)
}

// ----------------------------------------------------------------------------
// Private helpers
// ----------------------------------------------------------------------------

function executeGETRequest(url, token, options) {
  const headers = {
    Authorization: `Bearer ${token}`,
  }
  return executeRequest(url, "get", null, headers, options)
}

function executePOSTRequest(url, token, body, headers) {
  const newHeaders = headers || {}
  if (token) {
    newHeaders["Authorization"] = `Bearer ${token}`
  }
  return executeRequest(url, "post", body, newHeaders)
}

function executePUTRequest(url, token, body, headers) {
  const newHeaders = headers || {}
  if (token) {
    newHeaders["Authorization"] = `Bearer ${token}`
  }
  return executeRequest(url, "put", body, newHeaders)
}

function executeDELETERequest(url, token) {
  const headers = {
    Authorization: `Bearer ${token}`,
  }
  return executeRequest(url, "delete", null, headers)
}

// ----------------------------------------------------------------------------

async function executeFormDataRequest(givenUrl, method, headers, body, token) {
  let url = normalizeURL(givenUrl)

  const formData = new FormData()

  body && Object.keys(body).forEach((key) => formData.append(key, body[key]))

  const requestData = {
    method,
    headers: {
      ...headers,
      Authorization: `Bearer ${token}`,
    },

    body: formData,
  }

  try {
    const response = await fetch(url, requestData)

    let responseBody = {}

    if (response && response.ok) {
      const responseBodyText = await response.text()

      try {
        responseBody = JSON.parse(responseBodyText)
      } catch (e) {
        responseBody = responseBodyText
      }

      return responseBody
    } else {
      throw new APINotOkError(response.status, responseBody)
    }
  } catch (error) {
    throw new Error(error)
  }
}

function executeRequest(givenURL, method, givenBody, givenHeaders, options) {
  let url = normalizeURL(givenURL)
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...givenHeaders,
  }
  let body
  if (givenBody) {
    body = JSON.stringify(givenBody)
    if (DEBUG_API) {
      // eslint-disable-next-line
      console.debug(`[API/REQUEST/BODY]`, url, givenBody)
    }
  }
  const requestData = {
    method,
    headers,
    body,
  }
  if (DEBUG_API) {
    // eslint-disable-next-line
    console.debug(`[API/REQUEST]`, url, requestData)
  }

  return fetch(url, requestData).then((response) => {
    if (DEBUG_API) {
      // eslint-disable-next-line
      console.debug(`[API/RESPONSE]`, url, response)
    }

    return response.text().then(function (text) {
      let responseBody
      try {
        responseBody = JSON.parse(text)
      } catch (e) {
        responseBody = text
      }

      if (DEBUG_API) {
        // eslint-disable-next-line
        console.debug(`[API/RESPONSE/BODY]`, responseBody)
      }

      if (response.ok) {
        if (options && options.isList) {
          return {
            data: responseBody,
          }
        } else {
          return responseBody
        }
      } else {
        throw new APINotOkError(response.status, responseBody)
      }
    })
  })
}

// -------------------------------------

function normalizeURL(givenURL) {
  let isPathAbsolute =
    givenURL.indexOf("http://") === 0 || givenURL.indexOf("https://") === 0
  if (isPathAbsolute) {
    return givenURL
  } else {
    // let baseURL = `${window.location.protocol}//${window.location.hostname}:${DEFAULT_API_PORT}`

    let baseURL = `https://blueaccess.discontinuity.dev`
    return URLJoin(baseURL, givenURL.replace(/^\.\//, ""))
  }
}

function urlWithQueryParams(url, params) {
  const urlParams = Object.entries(params)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&")
  return `${url}?${urlParams}`
}

// ----------------------------------------------------------------------------
